// Parser CSV mínimo con soporte de comillas (suficiente para las plantillas).
// Cabeceras esperadas: ver PLANTILLAS abajo. Separador coma o punto y coma.

export function parseCsv(text: string): Record<string, string>[] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  const sep = text.split('\n')[0].includes(';') ? ';' : ',';

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === sep) {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cell);
      if (row.some((c) => c.trim() !== '')) rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += ch;
    }
  }
  row.push(cell);
  if (row.some((c) => c.trim() !== '')) rows.push(row);

  const [header, ...data] = rows;
  const keys = header.map((h) => h.trim().toLowerCase());
  return data.map((r) =>
    Object.fromEntries(keys.map((k, i) => [k, (r[i] ?? '').trim()])),
  );
}

export const PLANTILLAS = {
  clientes: {
    cabecera: 'nombre,email,telefono,perfiles,notas',
    ejemplo: 'María López,maria@ejemplo.com,600111222,"propietario,inquilino",Dueña del piso de Serrano',
    ayuda: 'perfiles: propietario / inquilino / comprador / inversor, separados por comas',
  },
  viviendas: {
    cabecera:
      'direccion,zona,tipo,estado,m2,habitaciones,banos,precio_alquiler,precio_venta,operacion,descripcion',
    ejemplo:
      'Calle Serrano 21 3ºA,Salamanca,piso,disponible,145,3,2,2100,0,alquiler,Piso clásico reformado',
    ayuda:
      'tipo: piso/atico/chalet/unifamiliar/estudio/local/otro · estado: disponible/reservada/alquilada/vendida/reforma · operacion: alquiler/venta/ambas',
  },
} as const;

// Lo que se escribe a mano en una hoja de cálculo: «Ático», «En reforma»,
// «Alquiler y venta». El CRM guarda los valores en minúscula y sin tilde, así
// que se normalizan aquí en vez de rechazar la fila por una tilde o una
// mayúscula.
const valor = (s: string | undefined) =>
  (s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
const EQUIVALENCIAS: Record<string, string> = {
  'en reforma': 'reforma',
  'alquiler y venta': 'ambas',
  'venta y alquiler': 'ambas',
};
const normalizado = (s: string | undefined) => EQUIVALENCIAS[valor(s)] ?? valor(s);

export function rowToClient(r: Record<string, string>) {
  return {
    nombre: r.nombre,
    email: r.email,
    telefono: r.telefono,
    profile_types: (r.perfiles ?? '')
      .split(/[,;/]/)
      .map((p) => valor(p))
      .filter(Boolean),
    notas: r.notas,
  };
}

export function rowToProperty(r: Record<string, string>) {
  return {
    direccion: r.direccion,
    zona: r.zona,
    tipo: normalizado(r.tipo) || 'piso',
    estado: normalizado(r.estado) || 'disponible',
    m2: Number(r.m2) || 0,
    habitaciones: Number(r.habitaciones) || 0,
    banos: Number(r.banos) || 0,
    precio_alquiler: Number(r.precio_alquiler) || 0,
    precio_venta: Number(r.precio_venta) || 0,
    operacion: normalizado(r.operacion) || 'alquiler',
    descripcion: r.descripcion,
  };
}

/** Lee el archivo en el juego de caracteres en que venga. Excel guarda el «CSV
 *  (delimitado por comas)» en Windows-1252, no en UTF-8: leído como UTF-8,
 *  «Martínez» llegaba como «Mart�nez». Primero UTF-8 estricto; si falla, 1252. */
export async function leerArchivo(file: File): Promise<string> {
  const bytes = await file.arrayBuffer();
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}
